import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { trackedFunction } from 'reactiveweb/function';
import { Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import {
  PhArrowRight,
  PhSparkle,
  PhShieldCheck,
  PhCalculator,
  PhTrophy,
  PhListChecks,
} from 'ember-phosphor-icons';
import type CareerPathwaysApi from 'tio-employee/services/career-pathways-api';
import type { PslfJourney, PslfProjection } from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { LivePslfData, DataSourceInfo } from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';

// Import our custom PSLF components
import ForgivenessFortuneTeller from 'tio-employee/components/reimagined/pslf/forgiveness-fortune-teller';
import ControlTower from 'tio-employee/components/reimagined/pslf/control-tower';
import ScenarioCard from 'tio-employee/components/reimagined/pslf/scenario-card';
import MilestoneBadge from 'tio-employee/components/reimagined/pslf/milestone-badge';
import PaymentValidationCard from 'tio-employee/components/reimagined/pslf/payment-validation-card';

interface PslfModel {
  clientId: string;
  pslfData: LivePslfData;
  dataSourceInfo: DataSourceInfo;
}

interface PslfDashboardSignature {
  Args: {
    model: PslfModel;
  };
}

class PslfDashboard extends Component<PslfDashboardSignature> {
  @service declare careerPathwaysApi: CareerPathwaysApi;
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get pslfSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    // Only mark as "live" if we have real substantive PSLF data (forms or payments).
    // The page also renders demo journey data (projections, scenarios, achievements)
    // so showing a green "Live Data" badge when we only have an empty profile is misleading.
    if (
      this.liveData.isLiveData &&
      (this.totalQualifyingPayments > 0 || this.liveData.forms.length > 0)
    ) {
      return 'live';
    }
    return 'demo';
  }

  get liveData() {
    return this.args.model.pslfData;
  }

  get totalQualifyingPayments() {
    return this.liveData.accounts
      .flatMap((a) => a.loans)
      .reduce((sum, l) => sum + l.qualifyingPayments, 0);
  }

  journey = trackedFunction(this, async () => {
    return this.careerPathwaysApi.fetchPslfJourney();
  });

  get isLoading() {
    return this.journey.isLoading;
  }

  get data(): PslfJourney | null {
    return this.journey.value ?? null;
  }

  /**
   * Merged projection - uses live data for qualifying payments when available,
   * but falls back to demo data for future projections.
   * Returns the demo projection, optionally merged with live qualifying payment data.
   */
  get projection(): PslfProjection {
    // Default projection values (used if no data loaded yet)
    const defaultProjection: PslfProjection = {
      qualifyingPaymentsToDate: 0,
      paymentsRemaining: 120,
      loanBalanceAtForgiveness: 0,
      projectedForgivenessAmount: 0,
      projectedForgivenessDate: '',
      totalPaidToDate: 0,
      totalWillPay: 0,
      forgivenessValue: 0,
      monthlyForgivenessRate: 0,
      yearlyForgivenessRate: 0,
      earnedToDate: 0,
    };

    const baseProjection = this.data?.projection || defaultProjection;

    if (this.liveData.isLiveData) {
      const qualifyingPayments = this.totalQualifyingPayments;
      const remaining = Math.max(0, 120 - qualifyingPayments);

      return {
        ...baseProjection,
        qualifyingPaymentsToDate: qualifyingPayments,
        paymentsRemaining: remaining,
        // Use live estimated forgiveness if available
        projectedForgivenessAmount:
          this.liveData.estimatorResults?.estimatedForgivenessAmount ||
          baseProjection.projectedForgivenessAmount,
        projectedForgivenessDate:
          this.liveData.estimatorResults?.estimatedForgivenessDate ||
          baseProjection.projectedForgivenessDate,
      };
    }

    return baseProjection;
  }

  get topAchievements() {
    // Show most recent 4 unlocked achievements
    if (!this.data?.achievements) return [];
    return this.data.achievements
      .filter((a) => a.achievedAt !== null)
      .sort((a, b) => new Date(b.achievedAt!).getTime() - new Date(a.achievedAt!).getTime())
      .slice(0, 4);
  }

  get recentPayments() {
    // Show most recent 3 payments from demo data
    // Note: Live forms are for ECF certification, not individual payment validation
    // Future: could integrate with loan servicer API for actual payment history
    if (!this.data?.recentPayments) return [];
    return this.data.recentPayments.slice(0, 3);
  }

  get recentForms() {
    // Use live form data if available for ECF history
    if (this.liveData.isLiveData && this.liveData.forms.length > 0) {
      return this.liveData.forms.slice(0, 3);
    }
    return [];
  }

  get topScenarios() {
    // Show 2 most relevant scenarios
    if (!this.data?.scenarios) return [];
    return this.data.scenarios.slice(0, 2);
  }

  get handleScenarioExplore() {
    return undefined;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="data-audit min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
    >
      {{#if this.isLoading}}
        {{! Loading state }}
        <div class="tio-row-center h-96">
          <div class="text-center">
            <div
              class="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4 border-[var(--brand)] border-t-transparent"
            ></div>
            <Text class="tio-fg-secondary">Loading your PSLF journey...</Text>
          </div>
        </div>
      {{else if this.data}}
        <DataIndicator @source={{this.pslfSource}} @showBadge={{true}} @dataSourceId="pslf-journey">
          {{! Hero Section - Fortune Teller }}
          <div class="relative">
            {{! Decorative background — soft brand blur behind the hero }}
            <div class="absolute inset-0 overflow-hidden">
              <div
                class="absolute top-20 left-1/4 w-96 h-96 rounded-full blur-3xl bg-[color-mix(in_srgb,var(--brand)_10%,transparent)]"
              ></div>
              <div
                class="absolute top-40 right-1/4 w-80 h-80 rounded-full blur-3xl bg-[color-mix(in_srgb,var(--brand)_10%,transparent)]"
              ></div>
            </div>

            <div class="relative z-10 max-w-7xl mx-auto px-6 py-12">
              {{! Welcome header }}
              <div class="mb-8">
                <div class="tio-row-tight mb-2">
                  <PhSparkle @size={{24}} class="tio-fg-brand" />
                  <Text @size="sm" @weight="semibold" class="tio-fg-brand uppercase tracking-wide">
                    PSLF Reimagined
                  </Text>
                </div>
                <Heading @level={{1}} class="tio-text-4xl-bold">
                  Your Path to Forgiveness
                </Heading>
                <Text @size="lg" class="tio-fg-secondary mt-2">
                  {{this.projection.qualifyingPaymentsToDate}}
                  qualifying payments and counting
                </Text>
              </div>

              {{! Main fortune teller - use merged projection with live data }}
              <ForgivenessFortuneTeller @projection={{this.projection}} class="mb-8" />

              {{! Quick action buttons }}
              <div class="tio-wrap-loose">
                <LinkTo
                  @route="authenticated.reimagined.pslf.payments"
                  class="tio-btn tio-btn--brand tio-btn--outlined tio-btn--md"
                >
                  <PhListChecks @size={{20}} />
                  <span>View All Payments</span>
                </LinkTo>
                <LinkTo
                  @route="authenticated.reimagined.pslf.scenarios"
                  class="tio-btn tio-btn--brand tio-btn--outlined tio-btn--md"
                >
                  <PhCalculator @size={{20}} />
                  <span>What-If Calculator</span>
                </LinkTo>
                <LinkTo
                  @route="authenticated.reimagined.pslf.milestones"
                  class="tio-btn tio-btn--brand tio-btn--outlined tio-btn--md"
                >
                  <PhTrophy @size={{20}} />
                  <span>Achievements</span>
                </LinkTo>
              </div>
            </div>
          </div>

          {{! Main content grid }}
          <div class="max-w-7xl mx-auto px-6 pb-12">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {{! Left column - Control Tower + Payments }}
              <div class="lg:col-span-2 space-y-8">
                {{! Control Tower }}
                <Section>
                  <div class="tio-row-between mb-4">
                    <div class="tio-row-tight">
                      <PhShieldCheck @size={{20}} class="tio-fg-brand" />
                      <Heading @level={{2}} @size="xl">
                        Autopilot Status
                      </Heading>
                    </div>
                  </div>
                  <ControlTower
                    @automationStatus={{this.data.automationStatus}}
                    @protectionStatus={{this.data.protectionStatus}}
                  />
                </Section>

                {{! Recent Payments }}
                <Section>
                  <div class="tio-row-between mb-4">
                    <div class="tio-row-tight">
                      <PhListChecks @size={{20}} class="tio-fg-brand" />
                      <Heading @level={{2}} @size="xl">
                        Recent Payments
                      </Heading>
                    </div>
                    <LinkTo
                      @route="authenticated.reimagined.pslf.payments"
                      class="tio-link-brand-inline tio-inline-row gap-1 text-sm"
                    >
                      <span>View All</span>
                      <PhArrowRight @size={{16}} />
                    </LinkTo>
                  </div>
                  <div class="space-y-4">
                    {{#each this.recentPayments as |payment|}}
                      <PaymentValidationCard @payment={{payment}} @compact={{true}} />
                    {{/each}}
                  </div>
                </Section>
              </div>

              {{! Right column - Scenarios + Achievements }}
              <div class="space-y-8">
                {{! What-If Scenarios }}
                <Section>
                  <div class="tio-row-between mb-4">
                    <div class="tio-row-tight">
                      <PhCalculator @size={{20}} class="tio-fg-brand" />
                      <Heading @level={{2}} @size="lg">
                        What-If Scenarios
                      </Heading>
                    </div>
                    <LinkTo
                      @route="authenticated.reimagined.pslf.scenarios"
                      class="tio-link-brand-inline tio-inline-row gap-1 text-sm"
                    >
                      <span>All</span>
                      <PhArrowRight @size={{16}} />
                    </LinkTo>
                  </div>
                  <div class="space-y-4">
                    {{#each this.topScenarios as |scenario|}}
                      <ScenarioCard
                        @scenario={{scenario}}
                        @onExplore={{this.handleScenarioExplore}}
                      />
                    {{/each}}
                  </div>
                </Section>

                {{! Achievements }}
                <Section>
                  <div class="tio-row-between mb-4">
                    <div class="tio-row-tight">
                      <PhTrophy @size={{20}} class="tio-fg-brand" />
                      <Heading @level={{2}} @size="lg">
                        Achievements
                      </Heading>
                    </div>
                    <LinkTo
                      @route="authenticated.reimagined.pslf.milestones"
                      class="tio-link-brand-inline tio-inline-row gap-1 text-sm"
                    >
                      <span>All</span>
                      <PhArrowRight @size={{16}} />
                    </LinkTo>
                  </div>
                  <div class="tio-flex-center flex-wrap gap-3">
                    {{#each this.topAchievements as |achievement|}}
                      <MilestoneBadge @achievement={{achievement}} @compact={{true}} />
                    {{/each}}
                  </div>
                  {{#unless this.topAchievements.length}}
                    <Text @size="sm" class="tio-fg-muted text-center">
                      Make your first qualifying payment to start earning achievements!
                    </Text>
                  {{/unless}}
                </Section>
              </div>
            </div>
          </div>

          {{! Bottom CTA — soft brand wash, tints automatically in dark mode }}
          <div class="py-12 bg-[color-mix(in_srgb,var(--brand)_15%,var(--bg-page))]">
            <div class="max-w-4xl mx-auto px-6 text-center">
              <Heading @level={{2}} class="font-bold mb-4">
                Questions about your PSLF journey?
              </Heading>
              <Text class="tio-fg-secondary mb-6">
                Our AI assistant can help you understand your eligibility, optimize your path, and
                answer any questions about Public Service Loan Forgiveness.
              </Text>
              <Button @intent="primary">
                <PhSparkle @size={{20}} />
                <span>Ask AI Assistant</span>
              </Button>
            </div>
          </div>
        </DataIndicator>
      {{else}}
        {{! Error state }}
        <div class="tio-row-center h-96">
          <div class="text-center">
            <Text class="tio-fg-secondary">Unable to load your PSLF data. Please try again.</Text>
            <Button @intent="primary" class="mt-4">
              <span>Retry</span>
            </Button>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}

export default PslfDashboard;
